import React, { useState, useEffect } from 'react';

const CalculatorList = ({ config }) => {
    const APP_URL = 'https://app.moneymarche.com';
    const WWW_URL = 'https://www.moneymarche.com';

    const calculatorCategories = [
        {
            title: 'Investment Calculators',
            icon: 'pi pi-chart-line',
            color: '#42A5F5',
            description: 'Plan your investments and understand the power of compounding',
            benefits: [
                'Make informed investment decisions',
                'Understand potential returns',
                'Plan for long-term wealth creation'
            ],
            calculators: [
                {
                    name: 'SIP Calculator',
                    icon: 'pi pi-chart-line',
                    path: '/calculators/sip',
                    description: 'Calculate your wealth growth through systematic investments',
                    extendedDescription: 'Systematic Investment Plans (SIP) help you invest regularly in mutual funds. Use this calculator to see how small, regular investments can grow into a substantial corpus over time through the power of compounding.',
                    benefits: [
                        'Estimate future wealth accumulation',
                        'Understand the power of compounding',
                        'Plan your investment amount based on goals'
                    ]
                },
                {
                    name: 'SWP Calculator',
                    icon: 'pi pi-chart-bar',
                    path: '/calculators/swp',
                    description: 'Plan your systematic withdrawals for regular income',
                    extendedDescription: 'Systematic Withdrawal Plans (SWP) allow you to receive regular income from your investments. Calculate how long your investments will last based on your withdrawal rate and expected returns.',
                    benefits: [
                        'Plan post-retirement income',
                        'Optimize withdrawal strategy',
                        'Maintain investment longevity'
                    ]
                },
                {
                    name: 'FD Calculator',
                    icon: 'pi pi-percentage',
                    path: '/calculators/fd',
                    description: 'Estimate returns on your fixed deposits',
                    extendedDescription: 'Fixed Deposits offer guaranteed returns. Use this calculator to compare FD returns across different tenures and interest rates, helping you make the best choice for your secure investments.',
                    benefits: [
                        'Compare different FD options',
                        'Calculate maturity amount',
                        'Plan for guaranteed returns'
                    ]
                },
                {
                    name: 'Compound Interest',
                    icon: 'pi pi-chart-pie',
                    path: '/calculators/compound-interest',
                    description: 'Understand how your money grows over time',
                    extendedDescription: 'Compound interest is the eighth wonder of the world. This calculator shows how your money grows exponentially when interest is earned on both principal and accumulated interest.',
                    benefits: [
                        'Visualize long-term growth',
                        'Compare different compounding frequencies',
                        'Understand the time value of money'
                    ]
                }
            ]
        },
        {
            title: 'Loan Calculators',
            icon: 'pi pi-money-bill',
            color: '#66BB6A',
            description: 'Plan your loans and understand repayment schedules',
            benefits: [
                'Optimize your borrowing decisions',
                'Plan repayment strategies',
                'Compare different loan options'
            ],
            calculators: [
                {
                    name: 'EMI Calculator',
                    icon: 'pi pi-money-bill',
                    path: '/calculators/emi',
                    description: 'Calculate your monthly loan installments',
                    extendedDescription: 'Equated Monthly Installment (EMI) is what you pay monthly towards your loan. This calculator helps you understand your loan costs and plan your budget effectively.',
                    benefits: [
                        'Calculate monthly payments',
                        'Understand total interest cost',
                        'Plan loan prepayment impact'
                    ]
                },
                {
                    name: 'Education Loan',
                    icon: 'pi pi-book',
                    path: '/calculators/education-loan',
                    description: 'Plan financing for higher education',
                    extendedDescription: 'Higher education is an investment in your future. Use this calculator to plan your education loan, understanding repayment schedules and interest costs during and after your studies.',
                    benefits: [
                        'Plan education financing',
                        'Understand repayment burden',
                        'Compare different loan terms'
                    ]
                },
                {
                    name: 'Home Loan Eligibility',
                    icon: 'pi pi-home',
                    path: '/calculators/home-loan-eligibility',
                    description: 'Check how much home loan you can get',
                    extendedDescription: 'Your home loan eligibility depends on various factors including income, existing obligations, and credit score. Calculate the maximum loan amount you can qualify for.',
                    benefits: [
                        'Know your loan eligibility',
                        'Plan property purchase budget',
                        'Understand EMI capacity'
                    ]
                }
            ]
        },
        {
            title: 'Insurance Calculators',
            icon: 'pi pi-shield',
            color: '#FFA726',
            description: 'Protect your future with the right insurance coverage',
            benefits: [
                'Ensure adequate protection',
                'Optimize premium payments',
                'Plan for contingencies'
            ],
            calculators: [
                {
                    name: 'Term Insurance',
                    icon: 'pi pi-shield',
                    path: '/calculators/term-insurance',
                    description: 'Calculate the coverage you need for life insurance',
                    extendedDescription: 'Term insurance provides financial security to your family. Calculate the optimal coverage amount based on your income, expenses, and future financial goals.',
                    benefits: [
                        'Determine coverage needs',
                        'Plan family security',
                        'Compare premium costs'
                    ]
                },
                {
                    name: 'Health Insurance',
                    icon: 'pi pi-heart',
                    path: '/calculators/health-insurance',
                    description: 'Plan your health coverage requirements',
                    extendedDescription: 'Medical expenses can be significant. Calculate the ideal health insurance coverage for you and your family based on age, medical history, and healthcare costs.',
                    benefits: [
                        'Estimate coverage needs',
                        'Plan family floater options',
                        'Compare premium costs'
                    ]
                },
                {
                    name: 'Motor Insurance',
                    icon: 'pi pi-car',
                    path: '/calculators/motor-insurance',
                    description: 'Estimate your vehicle insurance premium',
                    extendedDescription: 'Protect your vehicle with adequate insurance. Calculate premiums based on vehicle value, type, and coverage options to make an informed decision.',
                    benefits: [
                        'Calculate optimal coverage',
                        'Compare premium options',
                        'Plan for renewals'
                    ]
                },
                {
                    name: 'ULIP Calculator',
                    icon: 'pi pi-chart-line',
                    path: '/calculators/ulip',
                    description: 'Calculate returns on insurance-linked investments',
                    extendedDescription: 'Unit Linked Insurance Plans combine investment and insurance. Understand potential returns, insurance coverage, and charges to make the right investment choice.',
                    benefits: [
                        'Project investment returns',
                        'Understand fund options',
                        'Plan premium allocation'
                    ]
                }
            ]
        },
        {
            title: 'Financial Planning',
            icon: 'pi pi-shield',
            color: '#EC407A',
            description: 'Comprehensive tools for your financial journey',
            benefits: [
                'Create holistic financial plans',
                'Track financial health',
                'Achieve financial goals'
            ],
            calculators: [
                {
                    name: 'Emergency Fund',
                    icon: 'pi pi-shield',
                    path: '/calculators/emergency-fund',
                    description: 'Plan your safety net',
                    extendedDescription: 'An emergency fund is crucial for financial security. Calculate how much you need to save based on your monthly expenses and risk factors.',
                    benefits: [
                        'Build financial security',
                        'Plan for uncertainties',
                        'Maintain lifestyle stability'
                    ]
                },
                {
                    name: 'Net Worth',
                    icon: 'pi pi-wallet',
                    path: '/calculators/net-worth',
                    description: 'Track your financial position',
                    extendedDescription: 'Your net worth is a key indicator of financial health. Calculate and track your assets and liabilities to understand your true financial position.',
                    benefits: [
                        'Track financial progress',
                        'Identify wealth builders',
                        'Plan wealth growth'
                    ]
                },
                {
                    name: 'Financial Health',
                    icon: 'pi pi-heart',
                    path: '/calculators/financial-health',
                    description: 'Check your financial wellness score',
                    extendedDescription: 'Financial health is about more than just money. Assess your overall financial wellness across savings, investments, insurance, and debt management.',
                    benefits: [
                        'Assess financial stability',
                        'Identify improvement areas',
                        'Track progress over time'
                    ]
                }
            ]
        }
    ];

    const trackerFeatures = [
        {
            icon: 'pi pi-chart-line',
            title: 'Performance Analysis',
            description: 'Track historical returns and compare with benchmarks'
        },
        {
            icon: 'pi pi-chart-bar',
            title: 'Portfolio Insights',
            description: 'Analyze fund holdings and asset allocation'
        },
        {
            icon: 'pi pi-clock',
            title: 'Real-time Updates',
            description: 'Stay updated with latest NAV and market movements'
        },
        {
            icon: 'pi pi-file-pdf',
            title: 'Custom Reports',
            description: 'Generate detailed performance reports'
        }
    ];

    return (
        <div className="mm-calculator-list">
            <div className="mm-calculator-header">
                <h2>Financial Planning Tools</h2>
                <p className="mm-main-description">
                    Make informed financial decisions with our comprehensive suite of calculators and tracking tools. 
                    Whether you're planning investments, calculating loans, or managing insurance needs, our tools 
                    provide accurate insights for your financial journey.
                </p>
            </div>

            {/* Mutual Fund Performance Tracker Section */}
            <div className="mm-tracker-section">
                <div className="mm-tracker-content">
                    <h3>Mutual Fund Performance Tracker</h3>
                    <p>Track, analyze, and optimize your mutual fund investments with our advanced performance tracking tools.</p>
                    
                    <div className="mm-tracker-features">
                        {trackerFeatures.map((feature, index) => (
                            <div key={index} className="mm-tracker-feature">
                                <div className="mm-feature-icon">
                                    <i className={feature.icon}></i>
                                </div>
                                <div className="mm-feature-content">
                                    <h4>{feature.title}</h4>
                                    <p>{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <a 
                        href={`${WWW_URL}/product-compare-page`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mm-tracker-cta"
                    >
                        Start Tracking Your Investments
                    </a>
                </div>
            </div>

            <div className="mm-section-divider">
                <h3>Financial Calculators</h3>
                <p>
                    Explore our range of calculators designed to help you make better financial decisions. 
                    From investment planning to loan management, we've got you covered.
                </p>
            </div>

            <div className="mm-calculator-categories">
                {calculatorCategories.map((category, index) => (
                    <div key={index} className="mm-calculator-category">
                        <div className="mm-category-header" style={{ backgroundColor: category.color + '10' }}>
                            <i className={category.icon} style={{ color: category.color }}></i>
                            <h3>{category.title}</h3>
                            <p>{category.description}</p>
                            <ul className="mm-category-benefits">
                                {category.benefits.map((benefit, idx) => (
                                    <li key={idx}><i className="pi pi-check"></i>{benefit}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="mm-calculator-grid">
                            {category.calculators.map((calculator, calcIndex) => (
                                <div key={calcIndex} className="mm-calculator-extended-card">
                                    <div className="mm-calculator-main">
                                        <div className="mm-calculator-icon">
                                            <i className={calculator.icon} style={{ color: category.color }}></i>
                                        </div>
                                        <div className="mm-calculator-content">
                                            <h4>{calculator.name}</h4>
                                            <p>{calculator.description}</p>
                                        </div>
                                    </div>
                                    <div className="mm-calculator-extended">
                                        <p className="mm-extended-description">{calculator.extendedDescription}</p>
                                        <div className="mm-calculator-benefits">
                                            <h5>Benefits:</h5>
                                            <ul>
                                                {calculator.benefits.map((benefit, idx) => (
                                                    <li key={idx}><i className="pi pi-check"></i>{benefit}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <a 
                                            href={`${APP_URL}${calculator.path}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mm-calculator-cta"
                                        >
                                            Try Calculator
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CalculatorList; 